import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {SeasonalThemeService} from '../../season/seasonal-theme.service';

interface Floater {
	left: string;
	delay: string;
	duration: string;
	size: string;
	drift: string;
	spin: string;
}

interface Spark {
	x: number;
	y: number;
	vx: number;
	vy: number;
	life: number;
	max: number;
	color: string;
	size: number;
}

@Component({
	standalone: false,
	selector: 'ngx-season-atmosphere',
	templateUrl: './season-atmosphere.component.html',
	styleUrls: ['./season-atmosphere.component.scss'],
	host: {
		'[class.season-atmosphere--loader]': 'place === "loader"',
		'[class.season-atmosphere--pending]': 'place === "loader" && !attached',
		'[class.season-atmosphere--paused]': 'paused',
	},
})
export class SeasonAtmosphereComponent implements OnInit, OnDestroy {
	@Input() place: 'header' | 'loader' = 'header';

	mark: string | null = null;
	floaters: Floater[] = [];
	reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	attached = false;
	paused = false;

	private readonly subscriptions = new Subscription();
	private sparks: Spark[] = [];
	private frame = 0;
	private nextBurst = 0;
	private canvas: HTMLCanvasElement | null = null;
	private context: CanvasRenderingContext2D | null = null;
	private cssWidth = 0;
	private cssHeight = 0;
	private loaderObserver: MutationObserver | null = null;

	constructor(
		private seasonalTheme: SeasonalThemeService,
		private host: ElementRef<HTMLElement>,
	) {
	}

	@ViewChild('board')
	set board(ref: ElementRef<HTMLCanvasElement> | undefined) {
		const next = ref?.nativeElement ?? null;
		if (next === this.canvas) {
			return;
		}
		this.stopFireworks();
		this.canvas = next;
		this.context = this.canvas?.getContext('2d') ?? null;
		if (this.canvas && this.context && !this.reduced && !this.paused) {
			this.resize();
			this.nextBurst = performance.now() + 180;
			this.frame = requestAnimationFrame(this.draw);
		}
	}

	ngOnInit(): void {
		if (this.place === 'loader') {
			this.parkOnLoader();
		} else {
			this.attached = true;
		}
		this.subscriptions.add(this.seasonalTheme.chrome$.subscribe((chrome) => {
			const mark = chrome.animated ? chrome.mark : null;
			if (mark === this.mark) {
				return;
			}
			this.mark = mark;
			this.floaters = this.buildFloaters(mark);
			this.sparks = [];
		}));
		document.addEventListener('visibilitychange', this.onVisible);
	}

	ngOnDestroy(): void {
		this.stopFireworks();
		this.loaderObserver?.disconnect();
		document.removeEventListener('visibilitychange', this.onVisible);
		this.subscriptions.unsubscribe();
	}

	private parkOnLoader(): void {
		const spinner = document.getElementById('app-global-spinner');
		const center = spinner?.querySelector('.app-global-spinner__center');
		if (!spinner || !center) {
			return;
		}
		spinner.insertBefore(this.host.nativeElement, center);
		this.attached = true;
		this.syncLoader();
		this.loaderObserver = new MutationObserver(() => this.syncLoader());
		this.loaderObserver.observe(spinner, {attributes: true, attributeFilter: ['hidden', 'class']});
	}

	private syncLoader(): void {
		const spinner = document.getElementById('app-global-spinner');
		const hidden = !spinner || spinner.hasAttribute('hidden') || spinner.classList.contains('is-hidden');
		this.paused = hidden;
		if (hidden) {
			this.stopFireworks();
			return;
		}
		if (this.canvas && this.context && this.mark === 'glass' && !this.reduced && !this.frame) {
			this.frame = requestAnimationFrame(this.draw);
		}
	}

	private readonly onVisible = () => {
		if (document.visibilityState === 'hidden') {
			this.stopFireworks();
			return;
		}
		if (this.canvas && this.context && this.mark === 'glass' && !this.reduced && !this.paused) {
			this.frame = requestAnimationFrame(this.draw);
		}
	};

	private buildFloaters(mark: string | null): Floater[] {
		if (!mark || mark === 'glass' || mark === 'none' || this.reduced) {
			return [];
		}
		const band = this.place === 'header';
		const count = band
			? (mark === 'snowflake' ? 9 : mark === 'heart' ? 7 : 6)
			: (mark === 'snowflake' ? 18 : mark === 'heart' ? 12 : 8);
		return Array.from({length: count}, (_, index) => {
			const side = index % 2 === 0 ? 1 : -1;
			const size = band
				? (mark === 'snowflake' ? 10 + (index % 3) * 3 : mark === 'heart' ? 16 + (index % 3) * 4 : 13 + (index % 3) * 3)
				: (mark === 'snowflake' ? 16 + (index % 4) * 6 : mark === 'heart' ? 28 + (index % 4) * 8 : 22 + (index % 4) * 6);
			const duration = band ? 1.6 + (index % 4) * 0.28 : 2.4 + (index % 5) * 0.35;
			return {
				left: `${(index * 37) % 100}%`,
				delay: `${((index * 0.22) % (band ? 1.4 : 2.2)).toFixed(2)}s`,
				duration: `${duration.toFixed(2)}s`,
				size: `${size}px`,
				drift: `${side * (band ? 10 + (index % 22) : 28 + (index % 48))}px`,
				spin: `${side * (160 + (index % 90))}deg`,
			};
		});
	}

	private readonly draw = (now: number) => {
		if (!this.canvas || !this.context || document.visibilityState === 'hidden' || this.paused) {
			this.frame = 0;
			return;
		}
		this.resize();
		const compact = this.cssHeight < 180;
		const context = this.context;
		context.clearRect(0, 0, this.cssWidth, this.cssHeight);
		if (now >= this.nextBurst) {
			this.launch(compact);
			this.nextBurst = now + (compact ? 260 + Math.random() * 320 : 420 + Math.random() * 480);
		}
		const gravity = compact ? 0.012 : 0.06;
		this.sparks = this.sparks.filter((spark) => spark.life > 0);
		for (const spark of this.sparks) {
			spark.x += spark.vx;
			spark.y += spark.vy;
			spark.vy += gravity;
			spark.life -= 1;
			context.globalAlpha = Math.max(0, spark.life / spark.max);
			context.fillStyle = spark.color;
			context.beginPath();
			context.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2);
			context.fill();
		}
		context.globalAlpha = 1;
		this.frame = requestAnimationFrame(this.draw);
	};

	private launch(compact: boolean): void {
		if (!this.canvas || this.cssWidth < 2 || this.cssHeight < 2) {
			return;
		}
		const originX = this.cssWidth * (0.08 + Math.random() * 0.84);
		const originY = this.cssHeight * (compact ? 0.3 + Math.random() * 0.4 : 0.16 + Math.random() * 0.42);
		const tint = getComputedStyle(document.documentElement).getPropertyValue('--app-season-tint').trim() || '#E4C56A';
		const colors = [tint, '#fff6d0', '#ffffff', tint];
		const count = compact ? 12 + Math.floor(Math.random() * 6) : 34 + Math.floor(Math.random() * 12);
		for (let index = 0; index < count; index++) {
			const angle = (Math.PI * 2 * index) / count + Math.random() * 0.25;
			const speed = (compact ? 0.35 : 2.2) + Math.random() * (compact ? 0.55 : 2.6);
			const life = compact ? 14 + Math.floor(Math.random() * 10) : 18 + Math.floor(Math.random() * 12);
			this.sparks.push({
				x: originX,
				y: originY,
				vx: Math.cos(angle) * speed,
				vy: Math.sin(angle) * speed,
				life,
				max: life,
				color: colors[index % colors.length],
				size: compact ? 1 + Math.random() * 1.1 : 1.6 + Math.random() * 2.2,
			});
		}
	}

	private resize(): void {
		if (!this.canvas) {
			return;
		}
		const ratio = Math.min(window.devicePixelRatio || 1, 2);
		const cssWidth = this.canvas.clientWidth;
		const cssHeight = this.canvas.clientHeight;
		const width = Math.floor(cssWidth * ratio);
		const height = Math.floor(cssHeight * ratio);
		if (this.canvas.width !== width || this.canvas.height !== height) {
			this.canvas.width = width;
			this.canvas.height = height;
		}
		this.context?.setTransform(ratio, 0, 0, ratio, 0, 0);
		this.cssWidth = cssWidth;
		this.cssHeight = cssHeight;
	}

	private stopFireworks(): void {
		if (this.frame) {
			cancelAnimationFrame(this.frame);
			this.frame = 0;
		}
		this.sparks = [];
	}
}
